/**
 * @typedef {object} TextTruncateItem
 * @property {number} id - The ID of the item
 * @property {HTMLElement} element - The main element
 * @property {HTMLButtonElement} [toggleEl] - The toggle button element
 * @property {HTMLElement} contentEl - The content element
 * @property {string} contentRaw - The raw content of the element
 * @property {number} lengthSm - The length for small screens
 * @property {number} lengthMd - The length for medium screens
 * @property {number} lengthLg - The length for large screens
 */
import { debounce } from '../../utils/debounce.js';

export class TextTruncate {
	_selectors = {
		element: '.js-tk-text-truncate',
		content: '.js-tk-text-truncate__content',
		toggle: '.js-tk-text-truncate__toggle',
	};

	_styles = {
		toggle: 'c-tk-text-truncate__toggle',
		toggleHidden: 'c-tk-text-truncate__toggle--hidden',
		expanded: 'c-tk-text-truncate--is-expanded',
	};

	/**
	 *
	 * @type {Array.<TextTruncateItem>}
	 * @private
	 */
	_items = [];

	/**
	 * ResizeObserver instance to observe changes in window size
	 * @type {ResizeObserver | null}
	 * @private
	 */
	_resizeObserver = null;

	/**
	 * Previous window width to track changes
	 * @type {number}
	 * @private
	 */
	_previousWindowWidth = window.innerWidth;

	constructor() {
		this.init();
	}

	init() {
		this._initElements();

		this._resizeObserver = new ResizeObserver(
			debounce((entries) => {
				const windowWidth = window.innerWidth;

				if (this._previousWindowWidth === windowWidth) {
					return;
				}

				this._items.forEach((item) => this._truncateContent(item, windowWidth));
				this._previousWindowWidth = windowWidth;
			})
		);

		this._resizeObserver.observe(window.document.body);
	}

	update() {
		this._initElements();
	}

	_initElements() {
		const existingIds = this._items.map((item) => item.id);
		const startId = existingIds.length > 0 ? Math.max(...existingIds, -1) + 1 : 0;

		const elements = [...document.querySelectorAll(this._selectors.element)].filter(
			(el) => !el.hasAttribute('data-tk-text-truncate-initialized')
		);

		const items = elements.map((element, index) => {
			const id = startId + index;

			const contentEl = element.querySelector(this._selectors.content);
			const contentRaw = contentEl.innerHTML.replace(/\s+/g, ' ').trim();

			let toggleEl;
			const hasToggle = element.dataset.tkTextTruncateToggle !== undefined;

			if (hasToggle) {
				toggleEl = this._createToggle(element, id);
			}

			/**
			 * @type {TextTruncateItem}
			 */
			const item = {
				id,
				element,
				toggleEl,
				contentEl,
				contentRaw,
				lengthSm: element.dataset.tkTextTruncateLengthSm || 150,
				lengthMd: element.dataset.tkTextTruncateLengthMd || 300,
				lengthLg: element.dataset.tkTextTruncateLengthLg || 450,
			};

			// Mark element as initialized
			element.setAttribute('data-tk-text-truncate-initialized', 'true');

			this._placeToggleButton(item);

			return item;
		});

		// Add new items to the existing items array
		this._items = [...this._items, ...items];

		// Process only new items
		const windowWidth = window.innerWidth;

		items.forEach((item) => {
			this._truncateContent(item, windowWidth);
		});
	}

	/**
	 * @param {HTMLElement} element
	 * @param {number} id
	 * @returns {HTMLButtonElement}
	 * @private
	 */
	_createToggle(element, id) {
		const toggleEl = document.createElement('button');

		toggleEl.type = 'button';
		toggleEl.innerHTML = element.dataset.tkTextTruncateToggleLabelMore || 'Show more';
		toggleEl.classList.add(this._styles.toggle);

		toggleEl.addEventListener('click', (e) => {
			e.stopPropagation();
			this._toggleContent(id);
		});

		return toggleEl;
	}

	/**
	 * @param {number} id
	 * @private
	 */
	_toggleContent(id) {
		const windowWidth = window.innerWidth;
		const item = this._items.find((el) => el.id === id);

		if (!item) {
			throw new Error(`Text truncate item with id ${id} not found`);
		}

		const isExpanded = item.element.classList.contains(this._styles.expanded);

		if (isExpanded) {
			item.element.classList.remove(this._styles.expanded);
			item.toggleEl.innerHTML = item.element.dataset.tkTextTruncateToggleLabelMore || 'Show more';

			this._truncateContent(item, windowWidth);
		} else {
			item.element.classList.add(this._styles.expanded);
			item.toggleEl.innerHTML = item.element.dataset.tkTextTruncateToggleLabelLess || 'Show less';
			item.contentEl.innerHTML = item.contentRaw;

			this._placeToggleButton(item);
		}
	}

	/**
	 * Truncates the content of the element based on the screen size
	 * @param {TextTruncateItem} item
	 * @private
	 */
	_truncateContent(item, windowWidth) {
		const contentLength = item.contentRaw.length;

		let length = item.lengthLg;

		if (windowWidth < 768) {
			length = item.lengthSm;
		} else if (windowWidth < 1024) {
			length = item.lengthMd;
		}

		const isTruncated = contentLength > length;

		if (isTruncated) {
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = item.contentRaw;

			item.contentEl.innerHTML = this._truncateHTML(tempDiv, length);
			item.toggleEl?.classList.remove(this._styles.toggleHidden);
		} else {
			item.contentEl.innerHTML = item.contentRaw;
			item.toggleEl?.classList.add(this._styles.toggleHidden);
		}

		this._placeToggleButton(item);
	}

	/**
	 * Truncates HTML content while preserving tags
	 * @param {HTMLElement} element
	 * @param {number} maxLength
	 * @returns {string}
	 * @private
	 */
	_truncateHTML(element, maxLength) {
		let textLength = 0;
		let result = '';

		// Process all child nodes
		for (const node of Array.from(element.childNodes)) {
			if (textLength >= maxLength) break;

			if (node.nodeType === Node.TEXT_NODE) {
				// Text node
				const text = node.textContent;
				const remaining = maxLength - textLength;

				if (textLength + text.length <= maxLength) {
					result += text;
					textLength += text.length;
				} else {
					result += text.slice(0, remaining) + '…';
					textLength = maxLength;
				}
			} else if (node.nodeType === Node.ELEMENT_NODE) {
				// Element node
				const clone = node.cloneNode(false);
				const innerResult = this._truncateHTML(node, maxLength - textLength);

				if (innerResult) {
					clone.innerHTML = innerResult;
					result += clone.outerHTML;
					textLength += node.textContent.length;
				}
			}
		}

		return result;
	}

	/**
	 * Places the toggle button in the last element of content
	 * If no child elements exist, appends to the content element itself
	 * @param {TextTruncateItem} item
	 * @private
	 */
	_placeToggleButton(item) {
		if (!item.toggleEl) {
			return;
		}

		let targetElement = item.contentEl;

		const nodes = item.contentEl.childNodes;
		const lastNode = nodes[nodes.length - 1];

		if (lastNode?.nodeType === Node.ELEMENT_NODE) {
			targetElement = lastNode;
		}

		targetElement.appendChild(item.toggleEl);
	}
}

window.tkTextTruncate = new TextTruncate();
